import { Link, useLocation } from 'react-router'
import { Icon } from '#app/components/ui/icon.tsx'

export type BackTarget = { to: string; label: string }

// Human label for a known origin path. Hash/query are stripped so '/#writing'
// still resolves to "Home".
function labelFor(to: string): string {
	const path = to.split(/[#?]/)[0] || '/'
	if (path === '/') return 'Home'
	if (path === '/writing') return 'Writing'
	return 'Back'
}

// Where a "back" link should point. Prefers the origin recorded in navigation
// state (set via `backState()` on the source link) so an article opened from
// the home page returns Home, while the same article opened from /writing
// returns to the index. Falls back to `fallback` on a direct/refreshed load
// where no state exists.
export function useBackTarget(fallback: BackTarget): BackTarget {
	const location = useLocation()
	const from = (location.state as { from?: string } | null)?.from
	if (typeof from === 'string' && from.startsWith('/')) {
		return { to: from, label: labelFor(from) }
	}
	return fallback
}

// Pass to a <Link> so the destination knows where it was reached from.
export function backState(from: string) {
	return { from }
}

const DEFAULT_CLASS =
	'text-muted-foreground hover:text-foreground group mb-8 inline-flex items-center gap-1 text-sm transition-colors'

export function BackLink({
	fallback,
	className,
}: {
	fallback: BackTarget
	className?: string
}) {
	const { to, label } = useBackTarget(fallback)
	return (
		<Link to={to} className={className ?? DEFAULT_CLASS}>
			<Icon name="arrow-left" className="size-4" />
			{label}
		</Link>
	)
}
